import { SOCKET_SERVER } from 'config';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import io from 'socket.io-client';
import { useNotificationStore } from 'stores/notifications';

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

export const generateNotificationContent = (data) => {
  const transactions = Array.isArray(data?.data) ? data.data : [];

  return transactions.map((tx) => {
    const fullName = `${tx?.firstName || 'User'} ${tx?.lastName || ''}`.trim();
    const amount = tx?.amount
      ? `${tx.currency} ${Number(tx.amount).toLocaleString()}`
      : 'an amount';
    const method = tx?.transactionMethod || 'unknown method';
    const txType = tx?.transactionType?.toLowerCase() || 'transaction';
    const status = tx?.status?.toLowerCase() || 'unknown status';
    const bank = tx?.bankName || tx?.bankAccountName || '';
    const methodName = method.replace(/([A-Z])/g, ' $1');

    let title = 'New Transaction';
    let description = `${fullName} performed a ${txType} via ${methodName}.`;

    if (txType === 'deposit') {
      title = `Deposit ${capitalize(status)}`;
      description = `${fullName} has deposited ${amount} using ${methodName}${
        bank ? ` at ${bank}` : ''
      }.`;
    } else if (txType === 'withdraw') {
      title = `Withdrawal ${capitalize(status)}`;
      description = `${fullName} withdrew ${amount} via ${methodName}${
        bank ? ` from ${bank}` : ''
      }.`;
    } else if (txType === 'swap') {
      title = `Swap ${capitalize(status)}`;
      description = `${fullName} swapped ${amount} as ${
        method.includes('Sender') ? 'Sender' : 'Receiver'
      }.`;
    } else if (txType === 'transfer') {
      title = `Transfer ${capitalize(status)}`;
      description = `${fullName} transferred ${amount} as ${
        method.includes('Sender') ? 'Sender' : 'Receiver'
      }.`;
    } else {
      title = `Transaction ${capitalize(status)}`;
      description = `${fullName} made a ${txType} of ${amount} using ${methodName}${
        bank ? ` at ${bank}` : ''
      }.`;
    }

    return {
      title,
      description,
      type: ['failed', 'cancelled'].includes(status) ? 'error' : 'info',
    };
  });
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

const showLocalNotification = (title, description) => {
  if (
    document.hidden &&
    'Notification' in window &&
    Notification.permission === 'granted'
  ) {
    new Notification(title, {
      body: description,
      icon: '/icons/logo-192.png', // Optional: replace with your app's icon
    });
  }
};

export const SocketProvider = ({ token, children }) => {
  const socketRef = useRef(null);
  const [socketInstance, setSocketInstance] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const { addNotification } = useNotificationStore();
  const recentHashes = useRef(new Set());
   const notificationHashes = useRef(new Set());

  useEffect(() => {
    requestNotificationPermission();

    if (!token) return;

    const socket = io(SOCKET_SERVER, {
      transports: ['websocket'],
      reconnection: true,
    });

    socketRef.current = socket;
    setSocketInstance(socket);

    socket.on('connect', () => {
      setIsConnected(true);
      console.log('[Socket Connected]', socket.id);
      socket.emit('socket/admin/status', token, 'active');
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.warn('[Socket Disconnected]', reason);
      socket.emit('socket/admin/status', token, 'inactive');
    });

    socket.on('connect_error', (err) => {
      setIsConnected(false);
      console.error('[Socket Connection Error]', err);
    });

    socket.off('socket/register/error');
    socket.on('socket/register/error', (err) => {
      setError(err);
      console.error('[Socket Register Error]', err);
      addNotification({
        type: 'error',
        title: 'Socket Registration Failed',
        description: err?.message || 'An unknown socket error occurred.',
      });
    });

    socket.offAny();
   

    socket.onAny((eventName, ...args) => {
      const data = args[0];
      if (!data) return;

      const notifications = generateNotificationContent(data);

      notifications.forEach(({ title, description, type }) => {
        const hash = `${title}-${description}`.toLowerCase().trim();

        if (notificationHashes.current.has(hash)) return;

        notificationHashes.current.add(hash);
        setTimeout(() => notificationHashes.current.delete(hash), 10000); // keep for 10 seconds

        addNotification({ type, title, description });
        showLocalNotification(title, description);
      });
    });

    return () => {
      console.log('[Socket Cleanup]');
      socket.emit('socket/admin/status', token, 'inactive');
      socket.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (isConnected && socketRef.current && token) {
      console.log('[Socket Re-Emit] Active status on token update');
      socketRef.current.emit('socket/admin/status', token, 'active');
    }
  }, [isConnected, token]);

  const contextValue = useMemo(
    () => ({
      socket: socketInstance,
      isConnected,
      error,
    }),
    [socketInstance, isConnected, error]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};
